/**
 * The bench's user-visible strings (UX-WORD-02), kept apart from `strings.ts` so the bench can change on its own. Same
 * conventions: plain constants and small functions, no DOM; none may contain a word matched by `BANNED_WORDS`
 * (UX-WORD-01, checked by `test/unit/bench-strings.test.ts`). Voice: plain, short, specific.
 */

export const BENCH_NAV = {
	label: 'Bench',
} as const;

export const KEY = {
	noKey: 'No key',
	hasKey: 'Key entered',
	dialogTitle: 'Anthropic API key',
	label: 'API key',
	hint: 'Starts with sk-ant-. The key is used only to call Anthropic directly from this browser.',
	remember: 'Remember key on this device',
	save: 'Save',
	forget: 'Forget key',
	cancel: 'Cancel',
	empty: 'Enter a key first.',
} as const;

export const COST = {
	dialogTitle: 'Confirm the cost',
	model: 'Model',
	/** Option labels of the model select; the option values are the model ids. */
	modelNames: { sonnet: 'Sonnet 5.5 (recommended)', haiku: 'Haiku 5.5' },
	modelHint:
		'Sonnet 5.5 is the model the bench was measured with. Haiku 5.5 costs about a twentieth as much per token; its rewrite quality has not been measured.',
	effort: 'Effort',
	efforts: { low: 'Low', medium: 'Medium', high: 'High', xhigh: 'Extra high', max: 'Maximum' },
	/** ED-96: Haiku's measured time per request, shown while Haiku is chosen (ch62, 2026-10-14, the dev route). */
	haikuTime:
		'Measured on a test chapter: Haiku 5.5 at high effort took about 78 seconds per request and Sonnet 5.5 about 23. At the same effort level Haiku reasons at greater length, so it is the slower of the two.',
	/** The Standard path's calls at once (ED-86). */
	concurrency: 'Requests at once',
	concurrencyOption: (n: number) => (n === 1 ? '1 (one at a time)' : String(n)),
	concurrencyHint:
		'How many rewrite requests run at the same time. The bench starts at 4 and goes up to this number; if your API key’s rate limit pushes back, it slows down on its own. The answers are checked in the same order whatever this number is.',
	/** Versions per passage (BENCH-V3-PLAN §6): the model writes several named versions, the bench measures all and keeps the best. */
	versions: 'Versions per passage',
	versionsOption: (n: number) => (n === 1 ? '1 (one answer)' : `${n} versions`),
	versionsHint:
		'Each request asks the model for this many differently worked versions of every passage (a small change, a re-structured one, a fresh rewrite). The bench measures all of them, ignores the order the model wrote them in, and keeps the best, so a retry does not start from a failed draft. A request is longer and costs more, and the estimate includes that: on one test chapter two versions made each request about 1.8 times as long and the run about 1.5 times as dear, with fewer stretches left failed (one test is not a result). Sonnet starts at 2, Haiku at 3, and a document over 100,000 words at 2.',
	/** The keep standard (BENCH-V3-PLAN §7.1, ED-92): what the run keeps of a passage whose attempts all fell short. */
	keep: 'Keep standard',
	keepOptions: {
		strict: 'Strict (default)',
		lenient: 'Lenient',
	},
	keepHint:
		'Strict keeps a failed passage’s closest attempt only when it is safe and lowers the passage’s signal, as before. Lenient also keeps, after the run, the attempt of a failed passage that passed every meaning check and removed at least part of the pattern even though it failed a repetition, rhythm or signal check. Each lenient keep is marked, comes with a report of how it fell short, and can be reverted one by one or by kind. A changed number, a lost name, an altered quotation or a failed dialogue check is never kept under either standard.',
	/** The editorial guardrails (DESIGN ED-191, UD-41): a run option, off by default, unmeasured until the pilot. */
	guardrails: 'Editorial guardrails',
	guardrailsLabel: 'Editorial guardrails (experimental)',
	guardrailsHint:
		'Adds the editorial rules to every request: preserve meaning distinctions, voices and deliberate repetition; make the smallest sufficient change; leave a passage as it is, with a reason, when the flagged pattern does useful work or the passage is a record. At most two versions are asked for. Measured on 225 passages of a novel, one run each: rewrites failed a meaning check less often (5.0% of attempts against 9.4%) and changed fewer words (23 against 41 on average); 19% of the passages were left as they are, each with a reason, and fewer were fully addressed (18% against 42%; of the passages it rewrote, 19% still failed against 11%). A run costs about 40% more. Untick it to rewrite as before.',
	chunks: (n: string) => `${n} stretches targeted`,
	/** The document brief's line (BENCH-V3-PLAN §8): one read of the whole text before the rewrite, priced on its own. */
	brief: (n: string) =>
		n === '1'
			? 'Reading the whole document first (1 call, Sonnet 5.5)'
			: `Reading the whole document first (${n} calls, Sonnet 5.5)`,
	briefHint:
		'Before any rewrite the bench reads the document once and notes its repeated phrases, names and voices, so the rewrite requests can leave them alone. The notes are kept for this document; a second run on the same text does not pay for them again.',
	expected: 'Expected cost, with reasoning',
	max: (attempts: string) => `Maximum cost (up to ${attempts} attempts per stretch)`,
	economyNote:
		'Economy: the requests of each round go in one batch at about half the price. A run takes up to three rounds, and each round can take up to 24 hours. You can close the page and come back: the job resumes from its batches.',
	/** UD-38: a Standard run lives in the page; a long document is safer in Economy. */
	standardNote:
		'Standard runs in this page: if the page closes or reloads, or the computer sleeps, the run stops until you resume it. Long documents run more safely in Economy, which continues when the tab is closed.',
	nothing: 'Nothing in this scope can be rewritten: no flagged pattern is left to address.',
	confirm: 'Start',
	cancel: 'Cancel',
} as const;

export const BUDGET = {
	dialogTitle: 'This run passed its budget',
	body: (spent: string, limit: string) => `Spent $${spent} of the $${limit} limit.`,
	continueLabel: 'Continue with a higher limit',
	continueNote: 'The run continues from its last saved stretch and keeps what it has spent.',
	stop: 'Stop here',
} as const;

export const BENCH = {
	title: 'Bench',
	localTitle: 'Local pass',
	localHelp:
		'Deterministic text substitutions. No key, no network call, runs in under a few seconds.',
	transforms: {
		phrase: 'Phrase swaps',
		lex: 'Word swaps',
		scaffold: 'Remove scaffolding words',
		dash: 'Remove em and en dashes',
		pipe: 'Strip Markdown residue',
		quotes: 'Straighten quotation marks',
	},
	runLocal: 'Run local pass',
	localRunning: 'Running the local pass…',
	localDone: 'Local pass done.',
	deepTitle: 'Deep rewrite',
	deepHelp: 'Sends the targeted passages to the model, with up to three attempts each.',
	economyToggle: 'Economy (batches in up to three rounds, about half the cost)',
	rewriteFlagged: 'Rewrite the flagged sections',
	rewriteDocument: 'Address document-wide signals',
	repairTranslation: 'Reduce translation marks',
	rewriteChannel: "Rewrite this channel's flagged stretches",
	rewriteStretch: 'Rewrite this stretch',
	rewriteSelection: 'Rewrite selection',
	running: (done: string, total: string) => `Rewriting: ${done} of ${total} stretches`,
	/** The limiter during a parallel run (ED-86). */
	inFlight: (n: string, limit: string) => `${n} requests in flight (up to ${limit} at once now)`,
	done: 'Rewrite finished.',
	cancelled: 'Rewrite cancelled.',
	waiting: (round: string) => `Economy round ${round}: waiting for the batch (up to 24 hours)`,
	coverageTitle: 'Coverage',
	cancel: 'Cancel',
	enterKeyFirst: 'Enter an API key to start a rewrite.',
} as const;

export const LENS = {
	title: 'Before / after',
	/** Under the lens title when its run used the editorial guardrails (ED-191); nothing otherwise. */
	guardrailsOn: 'Run with editorial guardrails (experimental).',
	open: 'Show the before/after lens',
	close: 'Hide the lens',
	indexBefore: 'Before',
	indexAfter: 'After',
	channelsTitle: 'Channels, document scale',
	unitsTitle: 'Passages',
	chunksTitle: 'Stretches',
	chunkAccepted: 'accepted',
	chunkPartial: 'partially addressed',
	chunkFailed: 'failed',
	chunkSkipped: 'skipped',
	noRun: 'No rewrite has run yet.',
	marks: {
		accepted: 'accepted',
		partial: 'partially addressed',
		failed: 'failed',
		'kept-best': 'kept best (failed)',
		'kept-lenient': 'kept (lenient)',
		reverted: 'reverted',
		'not-selected': 'not selected',
		'not-actionable': 'not actionable',
		'document-route': 'document route',
		declined: 'left as it is',
	},
	keptBestMark: 'Kept best: the closest attempt that was safe, not a full fix',
	keptLenientMark:
		'Kept (lenient): the meaning checks passed and part of the pattern is gone, but the rewrite failed a stricter check. Read the report and revert it if you do not want it.',
	over: 'above its baseline',
	notRewritten: (reason: string) => `above its baseline, not rewritten (${reason})`,
	revertUnit: 'Revert to original',
	revertChunk: 'Revert this stretch to the original',
	revertAll: 'Revert all kept failures',
	revertLenient: 'Revert all lenient',
	revertSeverity: (severity: string) => `Revert all ${severity}`,
	severityFilter: 'Show passages by severity',
	severityAll: 'All',
	severityCount: (severity: string, kept: string, failed: string) =>
		`${severity}: ${kept} kept, ${failed} failed`,
	severities: {
		meaning: 'meaning',
		'signal-negative': 'signal-negative',
		style: 'style',
		cosmetic: 'cosmetic',
	},
	severityHint: {
		meaning: 'A meaning check or the dialogue check failed. Never kept.',
		'signal-negative':
			'The passage’s net signal is not positive, or a channel outside the repetition and rhythm channels rose.',
		style:
			'Only repetition and rhythm channels rose, if any (RH-01, RH-02, PT-02, ST-03, PROC-06, PROC-07). Often acceptable.',
		cosmetic: 'Only the length is outside the allowed range.',
	},
	report: {
		title: 'How it fell short',
		severity: (severity: string) => `Severity: ${severity}`,
		targeted: (channel: string, status: string, gone: string, of: string) =>
			of === '' ? `${channel} ${status}` : `${channel} ${status} (${gone} of ${of} spans gone)`,
		signal: (S: string, R: string, I: string, B: string) =>
			`Signal score ${S} (removed ${R}, added ${I}, carried ${B} points)`,
		net: (net: string) => `Net removal ${net}`,
		document: (channel: string, points: string, margin: string, phrase: string) =>
			`${channel} rose ${points} points for the whole document (margin ${margin})${phrase === '' ? '' : `: “${phrase}”`}`,
		window: (channel: string, para: string, cross: boolean) =>
			`${channel} rose near ¶${para}${cross ? ' (across the stretch edge)' : ''}`,
		introduced: (channel: string, spans: string, lost: boolean) =>
			`${lost ? 'Lost' : 'Introduced'} ${channel}${spans === '' ? '' : ` ${spans}`}`,
	},
	version: (strategy: string, others: string) =>
		others === '' ? `Version: ${strategy}.` : `Version: ${strategy}. The others: ${others}.`,
	versionNote: (strategy: string, outcome: string, reason: string) =>
		`${strategy} ${outcome === 'keep' ? 'left it as it is' : outcome === 'review' ? 'rejected by the review' : outcome}${reason === '' ? '' : ` (${reason})`}`,
	/** GR2 (ED-194): a passage the editor left as it is, in place of a diff; the reason is the model's one sentence. */
	declinedReason: (reason: string) => `Left as it is: ${reason}`,
	/** GR2: the rationale of a rewrite the editor made, under its diff. */
	why: (why: string) => `Why: ${why}`,
	/** GR3B: the meaning review's note on that rationale, when only its meaning judgements reject (never a failure). */
	reviewNoteTitle: 'Reason check',
	reviewNote: (note: string) =>
		`${note}. The meaning review found no change of meaning, so the rewrite stands.`,
	/** GR2: the lens list of the passages left as they are (only when there are some). */
	declined: 'Left as it is',
	declinedItem: (para: string, reason: string) => `¶${para}: ${reason}`,
	/** GR2: the export's title for the passages the editor left as they are. */
	declinedAppendixTitle: 'Left as it is',
	channelsWithLenient: 'With lenient keeps',
	channelsWithout: 'Without lenient keeps',
	contributors: (units: string) => `Raised by lenient keeps: ${units}`,
	bar: (failed: string, lenient: string, strict: string, n: string, upper: string) =>
		`${failed} of ${n} passages failed by the bar (${lenient} of them kept leniently; ${strict} more kept strictly and not counted); one-sided 95% upper bound ${upper}`,
	exportAppendix: 'List the lenient keeps at the end of an exported rewrite',
	appendixTitle: 'Kept with the lenient standard',
	/** The export's closing line for a version made with the editorial guardrails (ED-191), and its title when nothing else is listed. */
	guardrailsAppendix: 'Editorial guardrails: on (experimental).',
	guardrailsAppendixTitle: 'Rewrite settings',
	appendixItem: (para: string, channels: string, summary: string) =>
		`¶${para} (${channels}): ${summary}`,
	revertedNote: 'Reverted to the original text.',
	reverted: 'Reverted by the ledger',
	guardTitle: 'Dialogue checks',
	/** GR3 (ED-196): the prose review's sentences, under the guardrails only. */
	reviewTitle: 'Meaning review',
	signalTitle: 'Signal',
	/**
	 * The localised figure of each targeted channel (UD-45, shown in the lens since 2.32.0, UD-46): the channel's change-point
	 * segment that holds the passage. `span` is the segment's paragraphs, built in the component.
	 */
	localTitle: 'Where it lies',
	localSegment: (span: string, words: string, score: string, kappa: string) =>
		`segment ${span} (${words} words): score ${score}, data weight ${kappa}`,
	localFlags: { hotspot: 'hotspot', elevated: 'elevated', none: 'not elevated' },
	localDocument: 'no segment of its own; the document figure stands',
	signal: (rule: 'S' | 'events' | 'legacy', S: string, R: string, I: string, B: string) =>
		`${
			rule === 'events'
				? 'Judged by the event checks (the passage carried almost no signal)'
				: 'Judged by the signal score'
		}: score ${S}. Removed ${R} points, added ${I}, the passage carried ${B}.`,
	goalsTitle: 'Document-wide goals',
	goal: (channel: string, baseline: string, target: string, achieved: string) =>
		`${channel}: baseline ${baseline}, target ${target}, now ${achieved}`,
	coverageTitle: 'Coverage',
	coverage: (records: string, chunks: string) => `${records} of ${chunks} stretches measured`,
} as const;

/** The local pass's completion summary (UD-20). */
export const LOCAL_SUMMARY = {
	done: (n: string, kinds: string) => `Local pass done: ${n} changes of ${kinds} kinds.`,
	none: 'Local pass done: nothing to change in this text.',
	logTitle: 'What changed',
	colWhat: 'Substitution',
	colCount: 'Times',
	more: (n: string) => `and ${n} more kinds`,
	effectTitle: 'Effect on the channels',
	effectNone: 'No targeted channel was measurable before the pass.',
	exportHint:
		'The rewritten text is in Export, as plain text, Markdown, HTML or Word; the original is unchanged.',
} as const;

/** The rewrite's status summary, opened from the status bar (UD-20). */
export const SUMMARY = {
	title: 'Rewrite in progress',
	titleDone: 'Rewrite finished',
	titleStopped: 'Rewrite stopped',
	scope: 'What is being rewritten',
	scopes: {
		flagged: 'the flagged sections',
		document: 'document-wide signals',
		translation: 'translation marks (MT-01, TR-01; TR-02 protected)',
		channel: (id: string) => `the stretches flagged by ${id}`,
		hotspot: 'one hotspot stretch',
		selection: 'the selected text',
	},
	setup: 'Model and effort',
	setupLine: (modelEffort: string, route: string) =>
		modelEffort === '' ? route : `${modelEffort} · ${route}`,
	routeStandard: 'Standard (your key, several requests at once)',
	routeEconomy: 'Economy (batches, up to three rounds)',
	progress: 'Progress',
	progressLine: (done: string, total: string) => `${done} of ${total} stretches finished`,
	elapsed: (t: string) => `elapsed ${t}`,
	outcomes: 'Outcomes so far',
	outcomeLine: (accepted: string, partial: string, failed: string, untouched: string) =>
		`${accepted} accepted · ${partial} partially addressed · ${failed} failed · ${untouched} untouched`,
	outcomesNone: 'No stretch has finished yet.',
	/** GR2 (ED-194): the passages the editor left as they are, shown only for a finished job that ran with the guardrails. */
	declined: 'Left as it is',
	declinedLine: (n: string) => (n === '1' ? '1 passage' : `${n} passages`),
	last: 'Last stretch finished',
	lastLine: (where: string, status: string, rounds: string) =>
		`${where} · ${status} · ${rounds} attempts`,
	status: {
		accepted: 'accepted',
		partial: 'partially addressed',
		failed: 'failed',
		untouched: 'untouched',
	},
	cost: 'Cost',
	costLine: (spent: string, limit: string, requests: string) =>
		`$${spent} spent of the $${limit} limit · ${requests} requests`,
	tokens: (input: string, output: string, cached: string) =>
		`${input} input tokens, ${output} output, ${cached} read from cache`,
	economy: 'Economy round',
	economyLine: (round: string, waiting: boolean) =>
		`round ${round}: ${waiting ? 'waiting for the batch' : 'sending or checking requests'}`,
	/* ---- the live view of a Standard run (UD-20 adapted to bench v3: rounds in waves, answers applied in order) ---- */
	job: 'The job',
	document: 'Document',
	startedAt: 'Started',
	atOnce: 'Requests at once',
	atOnceLine: (n: string) => `up to ${n} (it starts at 4 and adapts, see Requests below)`,
	atOnceEconomy: 'not used (Economy sends each round as one batch)',
	versions: 'Versions per passage',
	versionsLine: (n: string) => (n === '1' ? 'one' : `${n} versions asked for each passage`),
	keep: 'Keep standard',
	keepStrict: 'strict (a passage is kept only if it passes every check)',
	keepLenient: 'lenient (a passage that misses only the score checks is kept and marked)',
	guardrails: 'Editorial guardrails',
	guardrailsOn: 'on (experimental: the editorial rules are in every request)',
	brief: 'Document brief',
	briefOn: 'on (every request carries the notes)',
	briefOff: 'not made for this job',
	riskSheet: 'Risk sheet',
	riskOn: 'on (every request lists the passage\u2019s repeats and limits)',
	round: 'Round',
	roundLine: (wave: string, max: string, done: string, total: string, pending: string) =>
		`${wave} of at most ${max}: ${done} of ${total} answers in, ${pending} to come`,
	roundTopUp: 'final pass on the goals that fell short',
	roundFinal: 'all rounds done; the final checks are running (no request goes out)',
	roundNone: 'the first answers are not in yet',
	shareDone: (share: string) => `${share} of the work done`,
	estimate: 'Time left',
	estimateNone: 'no estimate yet',
	requests: 'Requests',
	inFlight: 'In flight',
	inFlightLine: (flying: string, queued: string) =>
		`${flying} in flight now, ${queued} waiting for a free slot`,
	limit: 'At once now',
	limitLine: (n: string, max: string) =>
		`${n} (the limit starts at 4, drops when the service asks for a slower pace, and grows back; your choice is up to ${max})`,
	wasted: 'Not applied',
	wastedLine: (ahead: string, skipped: string) =>
		`${ahead} sent ahead and not needed, ${skipped} answers skipped because their stretch had changed`,
	slowdowns: 'Slowdowns',
	slowdownsNone: 'none so far',
	slowdownsLine: (n: string, last: string) => `${n} (the last at ${last})`,
	stretches: 'Stretches',
	table: {
		stretch: 'Stretch',
		state: 'State',
		rounds: 'Rounds',
		open: 'Open',
		addressed: 'Addressed',
		failed: 'Failed',
		waiting: 'waiting',
		inFlight: (round: string) => `request in flight (round ${round})`,
		applied: (round: string) => `answer applied (round ${round})`,
		finished: (status: string) => `finished: ${status}`,
		caption: (shown: string, total: string, flying: string, waiting: string, finished: string) =>
			`${shown} of ${total} stretches shown: ${flying} with a request in flight, ${waiting} waiting, ${finished} finished. Those in flight and the latest changes come first.`,
		none: 'The list of stretches arrives with the first answers.',
	},
	events: 'Recent events',
	eventsNone: 'Nothing has happened yet.',
	event: {
		started: (total: string) => `Rewrite started: ${total} stretches`,
		resumed: (done: string, total: string) =>
			`Rewrite resumed: ${done} of ${total} stretches were done`,
		wave: (wave: string, requests: string) => `Round ${wave} started: ${requests} requests`,
		answer: (where: string, round: string, accepted: string, rest: string, reason: string) =>
			`${where}, round ${round}: ${accepted} accepted${rest}${reason}`,
		notAccepted: (n: string) => `, ${n} not accepted`,
		reason: (r: string) => ` (${r})`,
		skipped: (where: string) => `${where}: the answer was skipped, the stretch had changed`,
		finished: (where: string, status: string) => `${where} finished: ${status}`,
		slowdown: (limit: string) => `Requests slowed to ${limit} at once (rate limit)`,
		brief: 'Document brief made',
	},
	whatNext: 'What happens next',
	whatNextRunning:
		'Each stretch gets up to three attempts; an attempt is kept only if it reduces the targeted evidence, adds no new hotspot elsewhere and keeps the meaning. You can close this window; the run continues, and the lens shows every outcome when it ends.',
	whatNextRounds:
		'The work goes in rounds. Every stretch is asked in round 1; a stretch with passages still failing is asked again in the next round, up to round 4. Answers are checked and applied in text order, even when they arrive in another order, so the result does not depend on timing. A stretch is finished when its passages are accepted or its rounds are used.',
	whatNextDone:
		'Open the reader to see the before/after lens, revert any stretch, and export the rewritten text.',
	detailsHint: 'Click for details',
	cancel: 'Cancel the rewrite',
	close: 'Close',
	noRun: 'No rewrite is running.',
} as const;

export const JOBS = {
	label: 'Jobs',
	none: 'No rewrite jobs yet.',
	running: 'running',
	paused: 'paused',
	done: 'done',
	cancelled: 'cancelled',
	error: 'error',
	open: 'Open',
	remove: 'Remove',
	resume: 'Resume',
	removeJob: (label: string) => `Remove ${label}`,
	/** UD-21 jobs list: a job that was started before the document's name was kept. */
	unnamed: 'Unnamed document',
	started: (when: string) => `Started ${when}`,
	stretches: (done: string, total: string) => `${done} of ${total} stretches`,
	costSoFar: (usd: string) => `$${usd} so far`,
	/** The jobs list row's and the overview's setup line, when the job ran with the editorial guardrails (ED-191). */
	guardrailsOn: 'guardrails on',
	/** GR2 (ED-194): a finished job's card, when it ran with the guardrails and the editor left passages as they are. */
	declined: (n: string) => `Left as it is: ${n}`,
	setup: (model: string, effort: string) => (effort === '' ? model : `${model}, ${effort} effort`),
	routes: { standard: 'Standard', economy: 'Economy' },
	sources: { file: 'File', paste: 'Pasted text', sample: 'Sample', url: 'Web page' },
	words: (n: string) => `${n} words`,
	excerpt: (words: string) => `“${words}”`,
	openLabel: (name: string, when: string) => `Open ${name}, started ${when}`,
	details: 'Details',
	detailsLabel: (name: string, when: string) =>
		`Show what the rewrite of ${name}, started ${when}, is doing now`,
	resumeLabel: (name: string, when: string) => `Resume ${name}, started ${when}`,
	removeLabel: (name: string, when: string) => `Remove ${name}, started ${when}`,
	/** UD-38: a Standard job another tab of this browser is running (its run lock is held there). */
	elsewhere: 'running in another tab',
	elsewhereNote:
		'Another tab of this browser is running this job. Resume and Remove are off here, so it is not run twice; to stop it, cancel it in that tab. This list follows its progress.',
	elsewhereTitle: 'Running in another tab',
	/** UD-38: why a run stopped, on the job card. `when` is a time ("00:41", or a date and time on another day). */
	stop: {
		/** The tail every Paused line ends with: the work done is kept. */
		resume: (done: string, total: string) =>
			`resume to carry on (${done} of ${total} stretches are kept)`,
		closed: (when: string, tail: string) =>
			`Paused: the page running it was closed or reloaded at ${when}; ${tail}`,
		hidden: (when: string, tail: string) =>
			`Paused: the page running it went into the background at ${when} and then stopped (closed, reloaded, or the computer slept); ${tail}`,
		offline: (when: string, tail: string) => `Paused: the network went offline at ${when}; ${tail}`,
		lost: (lastSeen: string | null, tail: string) =>
			`Paused: the page running it stopped (closed, reloaded, or the computer slept)${lastSeen ? `; it was last seen at ${lastSeen}` : ''}; ${tail}`,
		budget: (when: string, tail: string) =>
			`Paused at ${when}: the spending limit was reached; ${tail}`,
		error: (when: string) => `Stopped at ${when} by an error; open the job for what happened`,
	},
	progressLabel: (name: string) => `Progress of ${name}`,
	/** The header menu's entry: the name, the date and the status. */
	menuItem: (name: string, when: string, status: string, action = '') =>
		action === '' ? `${name} · ${when} · ${status}` : `${name} · ${action} · ${when} · ${status}`,
} as const;

/**
 * The action that produced a result, as a verb phrase in lower case (DESIGN UD-29): the overview's title joins the phrases of
 * every step from the original to the version it shows; the job card and the header menu name the job's own action.
 */
export const ACTION = {
	flagged: 'rewrite the flagged sections',
	document: 'address document-wide signals',
	translation: 'reduce translation marks',
	channel: (id: string) => `rewrite ${id}'s flagged stretches`,
	hotspot: (id: string) => `rewrite one ${id} hotspot`,
	selection: 'rewrite the selected text',
	local: 'run the local pass',
	/** Appended to the phrase of a step made with the editorial guardrails (ED-191): "rewrite the flagged sections, guardrails on". */
	guardrails: 'guardrails on',
	/** "Before and after address document-wide signals", "… both a and b", "… a, b and c". */
	before: (phrases: readonly string[]) =>
		phrases.length === 0
			? 'Before and after'
			: phrases.length === 1
				? `Before and after ${phrases[0]}`
				: phrases.length === 2
					? `Before and after both ${phrases[0]} and ${phrases[1]}`
					: `Before and after ${phrases.slice(0, -1).join(', ')} and ${phrases[phrases.length - 1]}`,
} as const;

/** The before/after overview of an opened rewrite (UD-22). */
export const OVERVIEW = {
	/** `lead` is the actions' phrase ("Before and after address document-wide signals"; UD-29). */
	title: (name: string, when: string, lead = 'Before and after') => `${lead}: ${name}, ${when}`,
	authorship: 'Machine-authorship index',
	translation: 'Machine-translation index',
	before: 'Before',
	after: 'After',
	change: 'Change',
	flagged: 'Flagged',
	notFlagged: 'Not flagged',
	uncalibrated: 'Uncalibrated',
	noValue: 'not available',
	reference: (x: string) => `Reference point ${x}`,
	uncalibratedNote:
		'This model is not calibrated, so the figure is the seed-rules composite and nothing is flagged.',
	translationNote: 'Shown only when the document has a translation index.',
	scopeNote:
		"Before is the original document (version 0) as plain text, whichever version the rewrite started from; after is the rewritten text as the run finished it, before any passage is reverted. Both come from the same full analysis of plain text that a pasted document gets, so a passage left unchanged scores the same on both sides. The before figures can differ slightly from the document overview, which also reads the original file's formatting.",
	channelsTitle: 'Every channel, document scale',
	channelsHelp:
		'The document-scale score of each channel (0 to 100) before and after the rewrite, with the change. A channel marked above its baseline rose past its baseline plus its floor after the rewrite. A channel that does not apply to the text shows not applicable. Channels are never merged.',
	cols: {
		id: 'Channel',
		name: 'Name',
		before: 'Before',
		after: 'After',
		change: 'Change',
		mark: 'Mark',
	},
	sortLabel: 'Sort the channels',
	sorts: { change: 'Largest change', id: 'Channel ID', after: 'Highest after' },
	notApplicable: 'not applicable',
	over: 'above its baseline',
	words: (before: string, after: string) => `${before} words before, ${after} after`,
	measuring: 'Measuring the rewritten text',
	measuringHelp:
		'The original and the rewritten text each go through the same analysis as a newly loaded document. On a long document this takes a while; the results are kept with the job, so opening it again is instant.',
	measuringBefore: 'Measuring the original text',
	cancelMeasure: 'Cancel',
	failed: (why: string) => `The rewritten text could not be measured: ${why}`,
	retry: 'Try again',
	showPassages: 'Show the passages',
	close: 'Close',
	reopen: 'Before and after overview',
	noAfter: 'The measurement of the rewritten text is not available.',
} as const;

/** How each channel compares with human writing of about the same length (DESIGN UD-24, ED-126, HUMAN-REFERENCE-DESIGN). */
export const HUMAN_REF = {
	title: 'Compared with human writing',
	help: 'Where each score falls among human documents of about this length (half to twice its words) in the categories selected below. Green is within the middle 90% of them; the colour turns yellow and then red the farther outside it falls. ↑ means higher than those documents (the machine-leaning side), ↓ lower. Select the categories closest to the text; a selection of fewer than about 200 documents of this length can show yellow but never red.',
	selector: 'Human reference',
	all: 'All categories',
	none: 'Clear',
	strata: {
		'H-FIC-LIT': 'Literary fiction',
		'H-FIC-GEN': 'Genre and popular fiction',
		'H-NF': 'Non-fiction',
		'H-ACA': 'Academic articles',
		'H-ESL': 'Non-native English',
		'H-WEB': 'Blogs',
		'H-NEWS': 'News',
		'H-ENC': 'Encyclopedia articles',
		'H-LAW': 'Law and regulation',
		'H-SPEECH': 'Speeches',
		'H-TRN': 'Human translations',
	} as Record<string, string>,
	stratumOption: (label: string, n: string) => `${label} (${n})`,
	cols: {
		before: 'Before vs human',
		after: 'After vs human',
		range: 'Human range (after)',
		doc: 'vs human',
		docRange: 'Human range',
	},
	range: (lo: string, hi: string) => `${lo}–${hi}`,
	n: (n: string) => `n ${n}`,
	pct: (p: string) => `p${p}`,
	aboveAll: (n: string) => `above all ${n}`,
	belowAll: (n: string) => `below all ${n}`,
	bands: {
		within: 'within the human range',
		outside: 'outside the human range',
		far: 'far outside the human range',
	},
	cell: (band: string, where: string, n: string) =>
		`${band}: ${where} of ${n} human documents of about this length`,
	unmeasured: 'unmeasured',
	unmeasuredHelp: (n: string) =>
		`Fewer than 30 human documents of this length in the selected categories (${n}).`,
	noneSelected: 'Select at least one category to compare with human writing.',
	loading: 'Loading the human reference…',
	failed: 'The human reference could not be loaded.',
	rangeHelp:
		"The middle 90% (5th to 95th percentile) of the channel's score in the human documents of about the rewritten text's length (the after side), and how many there are.",
	docRangeHelp:
		"The middle 90% (5th to 95th percentile) of the channel's score in the human documents of about this document's length, and how many there are.",
	legend: {
		within: 'within',
		outside: 'outside',
		far: 'far outside',
		up: '↑ higher than human writing',
		down: '↓ lower',
	},
	caveat:
		'Human writing is not uniform: of 3,170 human documents measured this way against all ten categories, two in three had at least one channel outside the human range and about one in eleven had a red one, more often for some kinds of writing (about one in four legal texts, one in forty encyclopedia articles). A colour shows how unusual one channel is for human writing; it is not evidence of authorship on its own.',
	counts: {
		channels: (k: number) => `${k} ${k === 1 ? 'channel' : 'channels'}`,
		own: (side: string, far: string, outside: string, measured: string) =>
			`${side}${far} red, ${outside} outside the human range in all (red included), of ${measured} measured.`,
		noneOutside: (side: string, measured: string) =>
			`${side}no channel outside the human range, of ${measured} measured.`,
		share: (n: string, far: string, farShare: string, outside: string, outsideShare: string) =>
			`Of the ${n} human documents of about this length in the selected categories, measured the same way, ${farShare} have ${far} or more red, and ${outsideShare} have ${outside} or more outside.`,
		shareOutsideOnly: (n: string, outside: string, outsideShare: string) =>
			`Of the ${n} human documents of about this length in the selected categories, measured the same way, ${outsideShare} have ${outside} or more outside.`,
		pct: (share: number) =>
			share === 0 ? 'none of them' : share < 0.01 ? 'under 1%' : `${Math.round(share * 100)}%`,
		unmeasured: (n: string) =>
			`Too few human documents of about this length in the selected categories (${n}) to compare these counts.`,
		pending: 'Counting the human documents…',
		help: 'Each human document is measured the way this text is, against the reference without its own fold (one fifth of the corpus), so its counts are those of a text the reference has not seen.',
		before: 'Before: ',
		after: 'After: ',
	},
	source: (n: string) =>
		`Reference: ${n} human documents in the selected categories, from the calibration corpus (never the held-out test slice).`,
} as const;

export const REWRITE_EXPORT = {
	txt: 'Rewritten text (plain text)',
	md: 'Latest version (Markdown)',
	html: 'Rewritten text (HTML)',
	docx: 'Rewritten text (Word)',
} as const;

/** The rewrite report download (DESIGN UD-44, operator 2026-10-21): the Export menu item, the lens button and the notices. */
export const REPORT_EXPORT = {
	menu: 'Rewrite report (Markdown)',
	lensButton: 'Download the rewrite report',
	lensButtonHelp:
		'A Markdown file for this run: where each rewritten passage is in the original, the signals it was rewritten for with their scores and the human range of each, and the document-wide signals the run addressed.',
	done: (filename: string) => `Rewrite report downloaded as ${filename}.`,
	failed: (why: string) => `The rewrite report could not be made: ${why}`,
	working: 'Building the rewrite report',
} as const;

/** The latest version as Markdown and re-analysed on request (DESIGN UD-30): the bench page, the version bar, the notices. */
export const LATEST = {
	reanalyse: 'Re-analyse the latest version',
	reanalyseHelp:
		"Analyses the latest version again in full: every view then shows it, and the next action's targets come from it. Use it before another round of rewrites.",
	nextOn: (n: string, made: string) =>
		n === '0'
			? 'Next action works on the original.'
			: `Next action works on version ${n}, after ${made}.`,
	viewsOnIt: 'The views show it.',
	viewsElsewhere: (shown: string) =>
		`The views show ${shown === '0' ? 'the original' : `version ${shown}`}; re-analyse the latest version to see its signals before the next action.`,
	viewsMeasuring: (n: string) => `Analysing version ${n}; the views switch to it when it is ready.`,
	download: 'Download (Markdown)',
	downloadLabel: (label: string) => `Download ${label} as Markdown`,
	markdownDone: (label: string, changed: string, fromSource: boolean) =>
		fromSource
			? `${label} downloaded as Markdown. ${changed === '1' ? 'One paragraph has' : `${changed} paragraphs have`} new text, with the original's heading, list and quote markers and its inline markup; everything else is exactly as in the uploaded file.`
			: `${label} downloaded as Markdown, with headings and paragraphs from the document's structure (the upload was not Markdown, or is no longer stored in this browser).`,
	markupMoved: (n: string) =>
		n === '1'
			? 'One emphasis or link whose words were changed is on the new words.'
			: `${n} emphases or links whose words were changed are on the new words.`,
	markupDropped: (list: string, more: string) =>
		`Inline markup not carried over (its words were deleted): ${list}${more === '0' ? '' : `, and ${more} more`}.`,
	markupItem: (para: string, text: string) => `paragraph ${para}, “${text}”`,
	/** Added to the download's notices when markup moved or was dropped: the way to check it (UD-31). */
	reviewTip:
		'Use "Review the Markdown" in the version bar to see them beside the original and fix them.',
} as const;

/** The review of a version's inline Markdown (DESIGN UD-31): the button, the screen and its notices. */
export const REVIEW = {
	open: 'Review the Markdown',
	openLabel: (label: string) => `Review the Markdown of ${label}`,
	openHelp:
		'Shows each rewritten paragraph beside the original, with the emphasis, bold, links and code that moved to new words or were dropped marked, and lets you fix any of it. Your changes are kept with the version and used by every download of it.',
	badge: (n: string) => (n === '1' ? '1 to check' : `${n} to check`),
	reading: 'Reading the rewritten paragraphs',
	title: (label: string) => `Review the Markdown of ${label}`,
	intro:
		'Each rewritten paragraph is shown as it was and as it will be downloaded. Emphasis, bold, links and code follow their words onto the new text; where the words were changed they move to the new words, and where the words were deleted they are dropped. Select words in the new paragraph and use Bold or Italic, or edit the Markdown directly.',
	summary: (paras: string, moved: string, dropped: string) =>
		`${paras === '1' ? '1 paragraph has' : `${paras} paragraphs have`} new text. ${moved} of the original's spans moved onto new words and ${dropped} were dropped.`,
	failed: (why: string) => `The review could not be made: ${why}`,
	retry: 'Try again',
	noParagraphs: 'This version has no rewritten paragraph that can carry inline markup.',
	look: (n: string) => `Check these (${n})`,
	lookNone: 'Nothing moved or dropped: every span stayed with its words.',
	rest: (n: string) => `Other rewritten paragraphs (${n})`,
	more: (n: string) => `Show ${n} more`,
	para: (n: string) => `Paragraph ${n}`,
	original: 'Original',
	rewritten: 'New (the download)',
	bold: 'Bold',
	italic: 'Italic',
	boldHelp: 'Bold on the selected words, or off again (Ctrl or ⌘ + B)',
	italicHelp: 'Italic on the selected words, or off again (Ctrl or ⌘ + I)',
	reset: 'Reset to the automatic version',
	edited: 'Edited by you',
	moved: 'Moved to new words',
	dropped: 'Dropped',
	staleChip: 'Text changed since your edit',
	spanMoved: (before: string, after: string) => `Moved: ${before} → ${after}`,
	spanDropped: (before: string) => `Dropped (its words were deleted): ${before}`,
	preview: 'Preview',
	textarea: (para: string) => `New Markdown of paragraph ${para}`,
	differs:
		'The words here are no longer the version’s words; the download will carry them as written. Reset to the automatic version to undo.',
	stale: 'This paragraph’s text changed after you edited it, so your edit is not used.',
	staleUse: 'Use my earlier edit',
	staleSee: (md: string) => `Your earlier edit: ${md}`,
	pending: (n: string) => (n === '1' ? '1 paragraph edited' : `${n} paragraphs edited`),
	save: 'Save',
	close: 'Close',
	closeDiscard: 'Close without saving',
	saved: (label: string, n: string) =>
		n === '0'
			? `Your Markdown edits of ${label} were removed; its downloads use the automatic Markdown.`
			: `${n === '1' ? 'One paragraph' : `${n} paragraphs`} of ${label} kept as you edited ${n === '1' ? 'it' : 'them'}; every download of ${label} uses ${n === '1' ? 'it' : 'them'}.`,
} as const;

/** The version chain (DESIGN UD-27): the version bar, its notices and the jobs list's place of a job. */
export const VERSIONS = {
	barLabel: 'Versions of this document',
	original: 'Original',
	version: (n: string) => `Version ${n}`,
	madeByLocal: (changes: string) => `local pass, ${changes} changes`,
	madeByJob: (scope: string) => scope,
	scopes: {
		flagged: 'Rewrite the flagged sections',
		document: 'Address document-wide signals',
		translation: 'Reduce translation marks',
		channel: (id: string) => `Rewrite for ${id}`,
		hotspot: (id: string) => `Rewrite a ${id} stretch`,
		selection: 'Rewrite a selection',
	},
	latest: 'latest',
	onScreen: 'on screen',
	view: 'View',
	viewLatest: 'View the latest version',
	showing: (n: string, latest: string) =>
		n === latest
			? `The views show version ${n}, the latest. The next action works on it; every before and after compares with the original.`
			: `The views show ${n === '0' ? 'the original' : `version ${n}`}. The next action works on version ${latest}, the latest.`,
	showingOriginal:
		'The views show the original. The next action works on it; every before and after compares with it.',
	analysing: (n: string) => `Analysing version ${n}`,
	analysingNote: (n: string) =>
		`Analysing version ${n} in full, as a pasted text. The views switch to it when it is ready.`,
	failed: (n: string, why: string) => `Version ${n} could not be analysed: ${why}`,
	retry: 'Try again',
	undo: 'Undo last step',
	undoHelp:
		'Removes the latest version and goes back to the one before. A rewrite job stays in the jobs list, marked undone; "Continue from this result" brings it back.',
	startAgain: 'Start again from the original',
	startAgainConfirm: (n: string) =>
		`This removes ${n === '1' ? 'version 1' : `versions 1 to ${n}`}; the jobs stay in the list. Start again from the original?`,
	startAgainYes: 'Start again',
	startAgainNo: 'Keep the versions',
	restored: (n: string) =>
		`This document has ${n === '1' ? 'one version' : `${n} versions`} from earlier work. The views show the latest; "Start again from the original" discards them.`,
	ask: {
		title: 'Restore the saved versions?',
		body: (n: string, label: string, made: string, when: string) =>
			`This document has ${n === '1' ? 'one version' : `${n} versions`} from earlier work. The latest is ${label}: ${made}, ${when}. Restoring them carries on from the latest; starting from the original discards them (the jobs stay in the list).`,
		busy: 'A rewrite is still running on this document, so the versions cannot be discarded now.',
		restore: (n: string) => (n === '1' ? 'Restore the version' : `Restore the ${n} versions`),
		original: 'Start from the original',
		bar: (n: string) =>
			`This document has ${n === '1' ? 'one saved version' : `${n} saved versions`}. The original is on screen; choose whether to restore them before the next step.`,
		choose: 'Choose',
		first:
			'This document has saved versions: restore them or start from the original first (the question above the views).',
	},
	outside: (n: string) =>
		`The rewrite finished, but it started from version ${n} and another step was added since, so its result was not added as a version. "Continue from this result" in the jobs list adds it once its starting version is the latest again.`,
	localOutside:
		'The local pass finished, but another step was added while it ran, so its result was not added as a version. Run it again.',
	notLatest:
		'A stretch or selection rewrite works on the latest version: view the latest version first, so its paragraphs are the ones on screen.',
	runningOn: (n: string) =>
		`A rewrite is running on ${n === '0' ? 'the original' : `version ${n}`}; its result becomes the next version when it finishes, unless another step is added first.`,
	exportOriginal: {
		txt: 'Original text (plain text)',
		md: 'Original text (Markdown)',
		docx: 'Original text (Word)',
	},
	place: {
		onOriginal: 'on the original',
		onVersion: (n: string) => `on version ${n}`,
		made: (n: string) => `made version ${n}`,
		unfinished: 'not a version until it finishes',
		undone: 'undone',
		outside: 'not in the versions',
	},
	adopt: 'Continue from this result',
	adoptLabel: (name: string, when: string) => `Continue from the result of ${name}, ${when}`,
	lensReadOnly:
		'This rewrite is not the latest step, so its passages cannot be reverted here. Reverts apply to the latest step only.',
	startedFrom: (n: string) =>
		n === '0'
			? 'This rewrite started from the original.'
			: `This rewrite started from version ${n}; before is still the original document.`,
} as const;

/**
 * What a rewrite message says (UD-32): every message names its cause and what to do next, and an outcome that is not a fault
 * (nothing to rewrite) is an information notice, not an error. `src/web/bench/messages.ts` picks and joins them.
 */
export const MESSAGES = {
	/** The disclosure that holds the original message. */
	detailsLabel: 'Details',
	/** The step that stopped. */
	lead: {
		local: 'The local pass stopped.',
		estimate: 'The rewrite could not start.',
		run: 'The rewrite stopped.',
		resume: 'The job could not resume.',
	},
	/** Nothing to rewrite (an outcome, with the reason). */
	nothing: {
		lead: (action: string, place: string) => `“${action}” found nothing to rewrite on ${place}.`,
		sent: 'Nothing was sent to Anthropic, so nothing was charged.',
		unknown: 'The rewrite found nothing to rewrite, and the analysis gave no further reason.',
		viewed: (viewed: string, latest: string) =>
			`The views show ${viewed}, but a rewrite always works on the latest version (${latest}), and that is the one analysed here.`,
		noSignals: (scored: string, docFired: string, docScored: string) =>
			`No channel fires on it: none of the ${scored} channels that scored reaches the firing threshold (${docFired} of ${docScored} document-wide channels fire), so there is no signal to address.`,
		noneScored: (abstained: string) =>
			`No channel could score it (${abstained} abstained, usually because the text is too short or too uniform), so there is no signal to address.`,
		onlyUnrewritable: (list: string) => `Only channels that cannot be rewritten fire: ${list}.`,
		noSignalsHere: (scored: string) =>
			`None of the ${scored} channels that scored fires around the selected paragraphs.`,
		noUnit: (list: string) => `The channels that fire (${list}) left no item a rewrite can act on.`,
		because: (reasons: string) => `Items left alone: ${reasons}.`,
		mtAbstained: (reason: string) =>
			`Neither the machine-translation channel (MT-01) nor the machine-leaning word-choice channel (TR-01) offered a sentence to rewrite: MT-01 did not score this version (${reason}), and TR-01 marked no word choice to replace.`,
		mtNone:
			'Neither the machine-translation channel (MT-01) nor the machine-leaning word-choice channel (TR-01) offered a sentence to rewrite: MT-01 scored this version but marked no sentence, and TR-01 marked no word choice to replace.',
		channelAbstained: (id: string, reason: string) =>
			`Channel ${id} did not score this version (${reason}).`,
		channelNot: (id: string) => `Channel ${id} does not fire on it.`,
		channelNotHere: (id: string) => `Channel ${id} does not fire around that stretch.`,
		channelFixed: (id: string, reason: string) =>
			`Channel ${id} fires but cannot be rewritten: ${reason}.`,
		channelNoUnit: (id: string) =>
			`Channel ${id} fires, but none of its items could be turned into a rewrite.`,
		next: {
			plain:
				'Choose another action for this version, or open the views to see where each channel stands.',
			viewed:
				'View the latest version to see the signals a rewrite acts on, or choose another action.',
		},
	},
	/** Faults: the cause, then the next step. */
	fault: {
		key: (status: string) => ({
			cause: `Anthropic refused the API key (${status}).`,
			next: 'Check the key in the key dialog (mistyped, revoked, or from another workspace) and start again.',
		}),
		permission: (status: string) => ({
			cause: `Anthropic says this key may not use the model or feature requested (${status}).`,
			next: 'Check the key’s workspace permissions and model access in the Anthropic console.',
		}),
		credit: {
			cause: 'The Anthropic account has run out of credit.',
			next: 'Add credit in the Anthropic console, then resume the job from the jobs list.',
		},
		notFound: (status: string) => ({
			cause: `Anthropic did not find the model or resource asked for (${status}).`,
			next: 'The model may have been retired. Reload the page to get the latest version of the app, then start again.',
		}),
		rejected: (status: string, msg: string) => ({
			cause: `Anthropic rejected the request as invalid (${status}: ${msg}).`,
			next: 'This is a fault in the app, not in your key. Resume the job from the jobs list; if it happens again, send the details.',
		}),
		tooLarge: (status: string) => ({
			cause: `The request or batch was too large for Anthropic (${status}).`,
			next: 'Choose a narrower scope, or fewer versions per passage, and start again.',
		}),
		rateLimit: (status: string) => ({
			cause: `Anthropic’s rate limit stopped the run (${status}), after the app waited and tried again several times.`,
			next: 'Wait a few minutes, then resume the job from the jobs list. A lower “requests at once” setting stays under the limit.',
		}),
		server: (status: string) => ({
			cause: `Anthropic’s service was unavailable or overloaded (${status}), after several tries.`,
			next: 'This is on their side. Resume the job from the jobs list in a few minutes.',
		}),
		network: {
			cause:
				'The browser could not reach Anthropic, or the connection dropped before the answer finished.',
			next: 'Check the connection, a VPN or a content blocker, then resume the job from the jobs list.',
		},
		relay: (code: string) => ({
			cause: `The Economy relay refused the batch request (${code}).`,
			next: 'Resume the job from the jobs list. If it refuses again, switch Economy off and start the rewrite again.',
		}),
		worker: {
			cause: 'The background worker stopped, most often because the page ran out of memory.',
			next: 'Reload the page. A job that had started is in the jobs list and resumes from its last saved stretch.',
		},
		lost: {
			cause: 'The rewrite lost its prepared state (an internal fault).',
			next: 'Start the action again.',
		},
		model: (msg: string) => ({
			cause: `The app refused to send the request (${msg}).`,
			next: 'This is a fault in the app. Send the details.',
		}),
		unknown: (msg: string) => ({
			cause: `The app met a fault it does not recognise (${msg}).`,
			next: 'Try again; if it repeats, send the details.',
		}),
	},
	/** A job that stopped earlier, opened from the list. */
	jobStopped: (why: string) => `This job stopped earlier. ${why}`,
	jobStatus: (status: string) => `This job is ${status} and holds no saved run state to resume.`,
	reviewElsewhere:
		'This job was run on another document. Open that document to review it, then open the job.',
	/** UD-38: a Resume or a second run of a job that another tab is running. */
	runningElsewhere:
		'This job is running in another tab of this browser, so it is not started a second time here. Cancel it in that tab, or wait for it to finish.',
	/** A job that cannot resume: the reason says why. */
	resumeRefused: (reason: string) => `This job cannot resume: ${reason}.`,
} as const;
